import { useState, useCallback, useRef } from 'react';
import { Upload, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import {
    parseLocalFeatureBundle,
    fetchNormativeModelsForFeatures,
    buildLocalPatientZScores
} from './localFeatureBundle';

// Preferred feature to color the brain by once an upload lands, in priority
// order. The first feature the bundle actually scored wins.
const DEFAULT_FEATURE_PRIORITY = [
    'bp_gamma',
    'bp_delta',
    'entropy',
    'fooof_exponent',
    'fooof_offset',
    'bp_beta',
    'bp_theta'
];

const chooseDefaultFeature = (featureIds) => (
    DEFAULT_FEATURE_PRIORITY.find(feature => featureIds.includes(feature)) || featureIds[0] || 'bp_gamma'
);

// Federated upload panel: a clinician processes a patient locally with
// pipeline/, then drops the resulting feature table here. Z-scores
// are computed in the browser against the normative model, so raw patient data
// never leaves the machine — only aggregate feature values are read locally.
export default function LocalPatientUpload({ montageType, onLocalPatientsLoaded }) {
    const [status, setStatus] = useState('idle');
    const [message, setMessage] = useState('');
    const [loadedPatients, setLoadedPatients] = useState([]);
    const [isDragging, setIsDragging] = useState(false);
    const inputRef = useRef(null);

    const processFile = useCallback(async (file) => {
        if (!file) {
            return;
        }
        if (!file.name.endsWith('.csv') && !file.name.endsWith('.zip')) {
            setStatus('error');
            setMessage('Upload a local feature .zip bundle or an aggregate feature .csv file.');
            return;
        }

        setStatus('loading');
        setMessage('Reading local feature bundle...');

        try {
            const bundle = await parseLocalFeatureBundle(file);
            if (!bundle.featureIds.length) {
                throw new Error('No recognized feature columns were found in this upload.');
            }

            const defaultFeature = chooseDefaultFeature(bundle.featureIds);
            setMessage('Loading normative model statistics...');
            const normativeModelMap = await fetchNormativeModelsForFeatures(bundle.featureIds, montageType);

            setMessage('Computing z-scores locally in this browser...');
            const zScoreData = buildLocalPatientZScores({
                bundle,
                selectedFeatures: bundle.featureIds,
                normativeModelMap
            });

            if (!zScoreData.length) {
                throw new Error('No z-scores could be computed. Check that the uploaded ROI names match the deployed normative atlas.');
            }

            const patientIds = zScoreData.map(patient => patient.patient_id);
            onLocalPatientsLoaded({ patientIds, zScoreData, feature: defaultFeature });

            setLoadedPatients(patientIds);
            setStatus('success');
            setMessage(
                `Loaded ${patientIds.length} local patient${patientIds.length === 1 ? '' : 's'} from ${bundle.site}. ` +
                `Computed ${bundle.featureIds.length} feature z-score${bundle.featureIds.length === 1 ? '' : 's'} in the browser — no patient data was sent to Neuronova.`
            );
        } catch (err) {
            console.error('LocalPatientUpload error:', err);
            setStatus('error');
            setMessage(err.message || 'An unexpected error occurred.');
        }
    }, [montageType, onLocalPatientsLoaded]);

    const onFileChange = (event) => {
        const file = event.target.files?.[0];
        if (file) processFile(file);
        event.target.value = '';
    };

    const onDrop = (event) => {
        event.preventDefault();
        setIsDragging(false);
        const file = event.dataTransfer.files?.[0];
        if (file) processFile(file);
    };

    const onDragOver = (event) => {
        event.preventDefault();
        setIsDragging(true);
    };
    const onDragLeave = () => setIsDragging(false);

    return (
        <div className="local-upload-panel">
            <div className="local-upload-header">
                <p className="local-upload-subtitle">
                    Run <code>pipeline/run.sh</code> on the clinician
                    workstation, then upload the resulting <code>*_combined_features.csv</code> here.
                    Z-scores are computed entirely in this browser — no patient data is transmitted.
                </p>
            </div>

            <div
                className={`local-upload-dropzone ${isDragging ? 'dragging' : ''} ${status === 'loading' ? 'loading' : ''}`}
                onClick={() => status !== 'loading' && inputRef.current?.click()}
                onDrop={onDrop}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
            >
                <input
                    ref={inputRef}
                    type="file"
                    accept=".zip,.csv,application/zip,text/csv"
                    style={{ display: 'none' }}
                    onChange={onFileChange}
                />
                {status === 'loading' ? (
                    <Loader size={32} className="local-upload-icon spin" />
                ) : status === 'success' ? (
                    <CheckCircle size={32} className="local-upload-icon success" />
                ) : status === 'error' ? (
                    <AlertCircle size={32} className="local-upload-icon error" />
                ) : (
                    <Upload size={32} className="local-upload-icon" />
                )}
                <p className="local-upload-dropzone-label">
                    {status === 'loading'
                        ? 'Processing...'
                        : status === 'success'
                        ? 'Upload another bundle'
                        : 'Drop local feature bundle here or click to select'}
                </p>
            </div>

            {message && (
                <div className={`local-upload-message ${status}`}>
                    {message}
                </div>
            )}

            {loadedPatients.length > 0 && (
                <div className="local-upload-patients">
                    <p className="local-upload-patients-label">Patients loaded locally:</p>
                    <ul>
                        {loadedPatients.map(pid => (
                            <li key={pid} className="local-upload-patient-id">{pid}</li>
                        ))}
                    </ul>
                    <p className="local-upload-privacy-note">
                        Data processed locally — no PHI transmitted.
                    </p>
                </div>
            )}

            <div className="local-upload-instructions">
                <p className="local-upload-instructions-title">How to prepare your data:</p>
                <ol>
                    <li>Run <code>pipeline/run.sh</code> on the clinician workstation.</li>
                    <li>Find the output CSV at <code>pipeline/output</code>.</li>
                    <li>Upload the <code>*_combined_features.csv</code> (or a <code>.zip</code> bundle) here.</li>
                </ol>
            </div>
        </div>
    );
}
